// EDEN · print font + paper-kind choices (Module P2b). One source of truth shared by the header
// card's pickers (test-editor.ts) and the print page's actual CSS (test-print.ts).
//
// Each stack lists the literal SYSTEM font name first, then our self-hosted webfont (fonts.css,
// loaded only on /tests/* — see that file's header). A teacher whose computer already has "Times
// New Roman" or the genuine "Khmer OS Muol Light" installed gets that exact rendering, pixel for
// pixel; everyone else gets the metric-twin/derivative webfont instead of a blank fallback.

export interface FontChoice {
  key: string;
  label: string;
  stack: string;
}

export const FONT_EN_OPTIONS: FontChoice[] = [
  { key: "times", label: "Times New Roman", stack: `"Times New Roman", Tinos, "Noto Serif", serif` },
  { key: "arial", label: "Arial", stack: `Arial, Arimo, "Noto Sans", sans-serif` },
  { key: "calibri", label: "Calibri", stack: `Calibri, Carlito, "Noto Sans", sans-serif` },
  { key: "cambria", label: "Cambria", stack: `Cambria, Caladea, "Noto Serif", serif` },
  { key: "georgia", label: "Georgia", stack: `Georgia, Gelasio, "Noto Serif", serif` },
  { key: "comic", label: "Comic Neue (young learners)", stack: `"Comic Sans MS", "Comic Neue", sans-serif` },
  { key: "noto-serif", label: "Noto Serif", stack: `"Noto Serif", serif` },
  { key: "noto-sans", label: "Noto Sans", stack: `"Noto Sans", sans-serif` },
];

export const FONT_KM_OPTIONS: FontChoice[] = [
  { key: "khmer-os-muol-light", label: "Khmer OS Muol Light (school-name style)", stack: `"Khmer OS Muol Light", Moul, "Khmer OS", Battambang, "Noto Sans Khmer", sans-serif` },
  { key: "moul", label: "Moul", stack: `Moul, "Khmer OS Muol Light", "Noto Serif Khmer", serif` },
  { key: "khmer-os", label: "Khmer OS", stack: `"Khmer OS", "Noto Sans Khmer", sans-serif` },
  { key: "khmer-os-siemreap", label: "Khmer OS Siemreap", stack: `"Khmer OS Siemreap", Siemreap, "Noto Sans Khmer", sans-serif` },
  { key: "battambang", label: "Battambang", stack: `Battambang, "Noto Sans Khmer", sans-serif` },
  { key: "hanuman", label: "Hanuman", stack: `Hanuman, "Noto Serif Khmer", serif` },
  { key: "noto-serif-khmer", label: "Noto Serif Khmer", stack: `"Noto Serif Khmer", serif` },
  { key: "noto-sans-khmer", label: "Noto Sans Khmer", stack: `"Noto Sans Khmer", sans-serif` },
];

export const DEFAULT_FONT_EN = "times";
export const DEFAULT_FONT_KM = "khmer-os-muol-light";

export function fontStack(options: FontChoice[], key: string | undefined, fallbackKey: string): string {
  return (options.find((o) => o.key === key) ?? options.find((o) => o.key === fallbackKey))!.stack;
}

export type PaperKind = "monthly" | "sa1" | "sa2" | "unit" | "worksheet";
export const PAPER_KINDS: PaperKind[] = ["monthly", "sa1", "sa2", "unit", "worksheet"];
export const PAPER_KIND_LABEL: Record<PaperKind, string> = {
  monthly: "Monthly Test",
  sa1: "Semestral Assessment 1",
  sa2: "Semestral Assessment 2",
  unit: "Unit Test",
  worksheet: "Worksheet",
};
export function isPaperKind(v: unknown): v is PaperKind {
  return typeof v === "string" && (PAPER_KINDS as string[]).includes(v);
}
